import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Grid,
  Column,
  Tile,
  TextInput,
  Select,
  SelectItem,
  InlineNotification,
  Loading,
  RadioButton,
  RadioButtonGroup,
  TextArea,
} from '@carbon/react';
import { ArrowLeft, Add, QrCode, Wifi, IbmCloudBareMetalServer } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useZones } from '../hooks/useZones';
import { useSubscription } from '../hooks/useSubscription';
import { addGateway } from '../services/gatewayService';
import { claimLilyGoGateway, kGatewaySoftApSsidFromMac } from '../services/lilyGoGatewayService';
import { supabase } from '../services/supabaseClient';
import { parseGatewayQr, ensureChillVerifyName } from '../utils/parseGatewayQr';
import QRCodeScanner from '../components/QRCodeScanner';
import SubscriptionGate from '../components/SubscriptionGate';

/**
 * Add Gateway — aligned with native:
 * 1) Choose KGateway vs LGateway (LilyGO / Lolin)
 * 2) KGateway: QR or MAC → register to account + SoftAP guidance
 * 3) LGateway: QR or claim code → claim-lilygo-gateway → SoftAP commission
 */
const AddGatewayPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { zones, isLoading: zonesLoading } = useZones();
  const { canAddGateways } = useSubscription();

  // 'kgateway' | 'lgateway' (LilyGO) | 'mgateway' (Lolin / Wemos)
  const [family, setFamily] = useState(null);
  const [step, setStep] = useState('choose'); // choose | method | claim | configure | success
  const [connectionType, setConnectionType] = useState('qr');
  const [formData, setFormData] = useState({
    name: '',
    zone_id: '',
    macAddress: '',
    claimCode: '',
    hardwareType: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [claimResult, setClaimResult] = useState(null);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const isEspClaimFamily = family === 'lgateway' || family === 'mgateway';
  const espProductName = family === 'mgateway' ? 'MGateway' : 'LGateway';

  const availableZones = useMemo(
    () => zones.map((zone) => ({ value: zone.id, label: zone.name })),
    [zones],
  );

  const resetToChoose = () => {
    setFamily(null);
    setStep('choose');
    setConnectionType('qr');
    setFormData({ name: '', zone_id: '', macAddress: '', claimCode: '', hardwareType: '' });
    setError('');
    setClaimResult(null);
  };

  const selectFamily = (value) => {
    setFamily(value);
    setStep('method');
    setError('');
    if (value === 'lgateway') {
      setFormData((prev) => ({
        ...prev,
        hardwareType: 'lilygo_tsim7670g',
        name: prev.name || '',
      }));
    } else if (value === 'mgateway') {
      setFormData((prev) => ({
        ...prev,
        hardwareType: 'wemos_lolin32',
        name: prev.name || '',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        hardwareType: 'kgateway',
      }));
    }
  };

  const assignZoneIfNeeded = async (gatewayId) => {
    if (!formData.zone_id || !gatewayId) return;
    const zone = zones.find((z) => z.id === formData.zone_id);
    if (!zone) return;
    const updatedGatewayIds = [...(zone.gateway_ids || []), gatewayId];
    await supabase.from('zones').update({ gateway_ids: updatedGatewayIds }).eq('id', zone.id);
  };

  const handleQRScanSuccess = useCallback(
    (qrData) => {
      setShowQRScanner(false);
      setError('');
      const parsed = parseGatewayQr(qrData);

      if (family === 'lgateway' || family === 'mgateway' || parsed.kind === 'lilygo') {
        if (parsed.kind === 'unknown' && !parsed.claimCode) {
          setError('QR not recognized as an LGateway or MGateway. Use a claim QR, or enter the claim code.');
          return;
        }
        const hw = parsed.hardwareType || (family === 'mgateway' ? 'wemos_lolin32' : 'lilygo_tsim7670g');
        const nextFamily = hw === 'wemos_lolin32' ? 'mgateway' : 'lgateway';
        setFamily(nextFamily);
        setFormData((prev) => ({
          ...prev,
          macAddress: parsed.macAddress || prev.macAddress,
          claimCode: parsed.claimCode || prev.claimCode,
          hardwareType: hw,
          name: ensureChillVerifyName(prev.name, parsed.macAddress || prev.macAddress),
        }));
        setStep('claim');
        return;
      }

      // KGateway path
      if (!parsed.macAddress) {
        setError('Could not read a MAC address from this QR. Try manual entry.');
        return;
      }
      setFamily('kgateway');
      setFormData((prev) => ({
        ...prev,
        macAddress: parsed.macAddress,
        hardwareType: 'kgateway',
        name: prev.name || `KGateway ${parsed.macAddress.replace(/:/g, '').slice(-6)}`,
      }));
      setStep('configure');
    },
    [family],
  );

  // Auto-claim when QR fills claim code
  useEffect(() => {
    if (step === 'claim' && formData.claimCode && connectionType === 'qr' && !claimResult && !isSubmitting) {
      // Leave user to confirm name / press Claim — don't auto-fire without consent on web
    }
  }, [step, formData.claimCode, connectionType, claimResult, isSubmitting]);

  const handleClaimLGateway = async () => {
    const code = formData.claimCode.trim();
    if (!code) {
      setError(`Claim code required. Enter the code from the ${espProductName} label or QR.`);
      return;
    }
    setIsSubmitting(true);
    setError('');
    const displayName = ensureChillVerifyName(formData.name, formData.macAddress);
    setFormData((prev) => ({ ...prev, name: displayName }));

    const result = await claimLilyGoGateway({
      claimCode: code,
      macAddress: formData.macAddress.trim() || undefined,
      name: displayName,
    });

    setIsSubmitting(false);
    if (!result.success) {
      setError(result.error || 'Claim failed');
      return;
    }

    setClaimResult(result);
    try {
      await assignZoneIfNeeded(result.gatewayId);
    } catch (e) {
      console.warn('Zone assign after claim failed', e);
    }
    setStep('success');
  };

  const handleAddKGateway = async () => {
    if (!formData.name || !formData.macAddress) {
      setError('Name and MAC address are required');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const macAddress = formData.macAddress;
      const inserted = await addGateway(macAddress, formData.name, macAddress, {
        gatewayType: 'kgateway',
        hardwareType: 'kgateway',
      });
      await assignZoneIfNeeded(inserted?.id);
      setClaimResult({
        success: true,
        gatewayId: inserted?.id,
        macAddress,
        kind: 'kgateway',
      });
      setStep('success');
    } catch (err) {
      console.error('Failed to add KGateway:', err);
      setError(err.message || 'Failed to add gateway');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (zonesLoading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading description="Loading data..." />
      </div>
    );
  }

  const softApSsidHint = kGatewaySoftApSsidFromMac(formData.macAddress || claimResult?.macAddress);

  return (
    <SubscriptionGate
      feature="adding gateways"
      disabled={!canAddGateways}
      customMessage="You need an active subscription to add gateways to your cold chain monitoring system."
    >
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '48px 0' }} data-testid="add-gateway-page">
        <Grid>
          <Column lg={{ span: 10, offset: 3 }} md={8} sm={4}>
            <Button
              kind="ghost"
              renderIcon={ArrowLeft}
              onClick={() => {
                if (step === 'choose') navigate('/dashboard/gateways');
                else if (step === 'method') resetToChoose();
                else if (step === 'claim' || step === 'configure') setStep('method');
                else if (step === 'success') navigate('/dashboard/gateways');
                else resetToChoose();
              }}
              style={{ marginBottom: '24px' }}
            >
              {step === 'choose' ? 'Back to Gateways' : 'Back'}
            </Button>

            <h1 style={{ color: textPrimary, fontSize: '2rem', fontWeight: 600, marginBottom: '8px' }}>
              Add Gateway
            </h1>
            <p style={{ color: textSecondary, marginBottom: '32px', lineHeight: 1.5 }}>
              Same options as the ChillVerify app: KGateway, LGateway, or MGateway.
            </p>

            {error && (
              <InlineNotification
                kind="error"
                title="Error"
                subtitle={error}
                onClose={() => setError('')}
                style={{ marginBottom: '24px' }}
              />
            )}

            {/* Step: Choose family */}
            {step === 'choose' && (
              <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px' }}>
                <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '8px' }}>
                  Which gateway are you adding?
                </h2>
                <p style={{ color: textSecondary, marginBottom: '24px', fontSize: '0.875rem' }}>
                  Match the hardware you received. You can also scan a QR later and we will route automatically.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <Button
                    kind="tertiary"
                    size="lg"
                    renderIcon={IbmCloudBareMetalServer}
                    onClick={() => selectFamily('kgateway')}
                    style={{ justifyContent: 'flex-start', maxWidth: '100%' }}
                  >
                    <span style={{ textAlign: 'left' }}>
                      <strong>KGateway</strong>
                      <br />
                      <span style={{ fontWeight: 400, fontSize: '0.8rem', opacity: 0.85 }}>
                        KKM SoftAP (beacongw_…). Scan QR or enter MAC to register, then provision Wi‑Fi on the device / app.
                      </span>
                    </span>
                  </Button>
                  <Button
                    kind="tertiary"
                    size="lg"
                    renderIcon={Wifi}
                    onClick={() => selectFamily('lgateway')}
                    style={{ justifyContent: 'flex-start', maxWidth: '100%' }}
                  >
                    <span style={{ textAlign: 'left' }}>
                      <strong>LGateway</strong>
                      <br />
                      <span style={{ fontWeight: 400, fontSize: '0.8rem', opacity: 0.85 }}>
                        Claim with QR or owner-card claim code, then configure site Wi‑Fi or LTE.
                      </span>
                    </span>
                  </Button>
                  <Button
                    kind="tertiary"
                    size="lg"
                    renderIcon={Wifi}
                    onClick={() => selectFamily('mgateway')}
                    style={{ justifyContent: 'flex-start', maxWidth: '100%' }}
                  >
                    <span style={{ textAlign: 'left' }}>
                      <strong>MGateway</strong>
                      <br />
                      <span style={{ fontWeight: 400, fontSize: '0.8rem', opacity: 0.85 }}>
                        Claim with QR or owner-card claim code, then configure site Wi‑Fi.
                      </span>
                    </span>
                  </Button>
                </div>
              </Tile>
            )}

            {/* Step: Method */}
            {step === 'method' && (
              <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px', overflow: 'visible' }}>
                <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '8px' }}>
                  {isEspClaimFamily ? `Claim ${espProductName}` : 'Add KGateway'}
                </h2>
                <p style={{ color: textSecondary, marginBottom: '24px', fontSize: '0.875rem' }}>
                  {isEspClaimFamily
                    ? 'Stay on internet (not ChillVerify SoftAP) while claiming. Scan the QR or type the claim code from the label.'
                    : 'Scan the gateway QR (MAC) or enter the MAC address printed on the device.'}
                </p>
                <RadioButtonGroup
                  name="connection-type"
                  valueSelected={connectionType}
                  onChange={(value) => setConnectionType(value)}
                  orientation="vertical"
                >
                  <RadioButton
                    id="qr"
                    value="qr"
                    labelText={
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <QrCode size={20} style={{ color: '#24a148', flexShrink: 0 }} />
                        <div>
                          <p style={{ color: textPrimary, fontWeight: 500, margin: 0 }}>Scan QR Code</p>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>
                            {isEspClaimFamily
                              ? `${espProductName} claim QR (JSON, URL, or LILYGO|MAC|CODE)`
                              : 'KGateway QR with MAC address'}
                          </p>
                        </div>
                      </div>
                    }
                  />
                  <RadioButton
                    id="manual"
                    value="manual"
                    labelText={
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <Add size={20} style={{ color: '#6929c4', flexShrink: 0 }} />
                        <div>
                          <p style={{ color: textPrimary, fontWeight: 500, margin: 0 }}>
                            {isEspClaimFamily ? 'Enter claim code' : 'Enter MAC address'}
                          </p>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>
                            {isEspClaimFamily
                              ? `Type CLAIM-XXXX from the ${espProductName} owner card`
                              : 'Manual MAC entry'}
                          </p>
                        </div>
                      </div>
                    }
                  />
                </RadioButtonGroup>
                <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
                  <Button kind="secondary" onClick={resetToChoose}>Back</Button>
                  <Button
                    kind="primary"
                    onClick={() => {
                      if (connectionType === 'qr') {
                        setShowQRScanner(true);
                      } else {
                        setStep(isEspClaimFamily ? 'claim' : 'configure');
                      }
                    }}
                  >
                    Continue
                  </Button>
                </div>
              </Tile>
            )}

            {/* LGateway / MGateway claim form */}
            {step === 'claim' && isEspClaimFamily && (
              <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px' }}>
                <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '8px' }}>
                  Claim {espProductName}
                </h2>
                <p style={{ color: textSecondary, marginBottom: '24px', fontSize: '0.875rem', lineHeight: 1.5 }}>
                  Claim uses the internet. Stay off the ChillVerify board network until after claim.
                  Then join <strong>ChillVerify_&lt;serial&gt;</strong> only when sending site Wi‑Fi
                  {family === 'lgateway' ? ' / LTE' : ''}.
                </p>
                <div style={{ marginBottom: '16px' }}>
                  <TextInput
                    id="claimCode"
                    labelText="Claim code *"
                    placeholder="CLAIM-XXXX"
                    value={formData.claimCode}
                    onChange={(e) => setFormData({ ...formData, claimCode: e.target.value })}
                    autoComplete="off"
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <TextInput
                    id="macAddress"
                    labelText="MAC address (optional)"
                    placeholder="80:B5:4E:CD:25:0C"
                    value={formData.macAddress}
                    onChange={(e) => setFormData({ ...formData, macAddress: e.target.value })}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <TextInput
                    id="name"
                    labelText="Display name (always chillverify_…)"
                    placeholder="chillverify_cold_room"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    onBlur={() =>
                      setFormData((prev) => ({
                        ...prev,
                        name: ensureChillVerifyName(prev.name, prev.macAddress),
                      }))
                    }
                    helperText="Normalized to chillverify_…. SoftAP SSID stays ChillVerify_<serial>."
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Select
                    id="hardwareType"
                    labelText="Hardware"
                    value={formData.hardwareType || (family === 'mgateway' ? 'wemos_lolin32' : 'lilygo_tsim7670g')}
                    onChange={(e) => {
                      const hw = e.target.value;
                      setFormData({ ...formData, hardwareType: hw });
                      setFamily(hw === 'wemos_lolin32' ? 'mgateway' : 'lgateway');
                    }}
                  >
                    <SelectItem value="lilygo_tsim7670g" text="LGateway" />
                    <SelectItem value="wemos_lolin32" text="MGateway" />
                  </Select>
                </div>
                {availableZones.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <Select
                      id="zone_id"
                      labelText="Assign to Zone (Optional)"
                      value={formData.zone_id}
                      onChange={(e) => setFormData({ ...formData, zone_id: e.target.value })}
                    >
                      <SelectItem value="" text="Select zone" />
                      {availableZones.map((zone) => (
                        <SelectItem key={zone.value} value={zone.value} text={zone.label} />
                      ))}
                    </Select>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <Button kind="secondary" onClick={() => setStep('method')}>Back</Button>
                  <Button kind="ghost" renderIcon={QrCode} onClick={() => setShowQRScanner(true)}>
                    Scan QR
                  </Button>
                  <Button kind="primary" onClick={handleClaimLGateway} disabled={isSubmitting || !formData.claimCode.trim()}>
                    {isSubmitting ? 'Claiming…' : `Claim ${espProductName}`}
                  </Button>
                </div>
              </Tile>
            )}

            {/* KGateway configure */}
            {step === 'configure' && family === 'kgateway' && (
              <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px' }}>
                <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '24px' }}>
                  Register KGateway
                </h2>
                <div style={{ marginBottom: '16px' }}>
                  <TextInput
                    id="name"
                    labelText="Gateway Name *"
                    placeholder="e.g., Main Cold Room Gateway"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <TextInput
                    id="macAddress"
                    labelText="MAC Address *"
                    placeholder="AA:BB:CC:DD:EE:FF"
                    value={formData.macAddress}
                    onChange={(e) => setFormData({ ...formData, macAddress: e.target.value })}
                  />
                </div>
                {availableZones.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <Select
                      id="zone_id"
                      labelText="Assign to Zone (Optional)"
                      value={formData.zone_id}
                      onChange={(e) => setFormData({ ...formData, zone_id: e.target.value })}
                    >
                      <SelectItem value="" text="Select zone" />
                      {availableZones.map((zone) => (
                        <SelectItem key={zone.value} value={zone.value} text={zone.label} />
                      ))}
                    </Select>
                  </div>
                )}
                <InlineNotification
                  kind="info"
                  title="First-time Wi‑Fi (SoftAP)"
                  subtitle={
                    softApSsidHint
                      ? `After registering, join Wi‑Fi “${softApSsidHint}” (or beacongw_<MAC>) and finish MQTT/Wi‑Fi provision in the ChillVerify mobile app. Browsers on HTTPS cannot talk to 192.168.8.1 SoftAP.`
                      : 'After registering, use the ChillVerify mobile app to join beacongw_<MAC> SoftAP and push home Wi‑Fi + MQTT. Full SoftAP provision is native-only on HTTPS sites.'
                  }
                  hideCloseButton
                  style={{ marginBottom: '24px' }}
                />
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Button kind="secondary" onClick={() => setStep('method')}>Back</Button>
                  <Button
                    kind="primary"
                    renderIcon={Add}
                    onClick={handleAddKGateway}
                    disabled={isSubmitting || !formData.name || !formData.macAddress}
                  >
                    {isSubmitting ? 'Adding…' : 'Add KGateway'}
                  </Button>
                </div>
              </Tile>
            )}

            {/* Success */}
            {step === 'success' && (
              <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px' }}>
                <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>
                  {claimResult?.kind === 'kgateway' || family === 'kgateway'
                    ? 'KGateway registered'
                    : `${espProductName} claimed`}
                </h2>

                {claimResult?.deviceApiKey && (
                  <div style={{ marginBottom: '16px' }}>
                    <TextArea
                      id="deviceApiKey"
                      labelText="Device API key (save now)"
                      value={claimResult.deviceApiKey}
                      readOnly
                      rows={2}
                    />
                    <Button
                      kind="ghost"
                      size="sm"
                      style={{ marginTop: 8 }}
                      onClick={() => {
                        navigator.clipboard?.writeText(claimResult.deviceApiKey);
                      }}
                    >
                      Copy key
                    </Button>
                  </div>
                )}

                {claimResult?.mqttProvisioning && (
                  <InlineNotification
                    kind="success"
                    title="MQTT provisioned"
                    subtitle={`Broker: ${claimResult.mqttProvisioning.host || '—'}:${claimResult.mqttProvisioning.port || ''}`}
                    hideCloseButton
                    style={{ marginBottom: '16px' }}
                  />
                )}

                {isEspClaimFamily && (
                  <InlineNotification
                    kind="info"
                    title="Next: configure network"
                    subtitle={`Join ChillVerify_<serial> SoftAP, then send site Wi‑Fi${family === 'lgateway' ? ' (or LTE on LGateway)' : ''}. On HTTPS web, use the ChillVerify app for SoftAP — or open Configure Network if this browser can reach 192.168.4.1.`}
                    hideCloseButton
                    style={{ marginBottom: '24px' }}
                  />
                )}

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {isEspClaimFamily && claimResult?.gatewayId && (
                    <Button
                      kind="primary"
                      onClick={() =>
                        navigate('/dashboard/gateways/commission', {
                          state: {
                            gatewayId: claimResult.gatewayId,
                            macAddress: claimResult.macAddress || formData.macAddress,
                            deviceApiKey: claimResult.deviceApiKey,
                          },
                        })
                      }
                    >
                      Configure network
                    </Button>
                  )}
                  {claimResult?.gatewayId && (
                    <Button
                      kind="tertiary"
                      onClick={() => navigate(`/dashboard/gateways/${claimResult.gatewayId}`)}
                    >
                      Open gateway
                    </Button>
                  )}
                  <Button kind="secondary" onClick={() => navigate('/dashboard/gateways')}>
                    Done
                  </Button>
                </div>
              </Tile>
            )}
          </Column>
        </Grid>

        <QRCodeScanner
          isOpen={showQRScanner}
          onClose={() => setShowQRScanner(false)}
          onScanSuccess={handleQRScanSuccess}
        />
      </div>
    </SubscriptionGate>
  );
};

export default AddGatewayPage;
